import React, { createContext, useContext, useState, useEffect } from "react";
import hosys, { getMemToken } from "@/api/api";

const VenueContext = createContext(null);

const SUPERADMIN_EMAILS = ["upp470@gmail.com"];

export function VenueProvider({ children }) {
  const [currentVenue, setCurrentVenue] = useState(null);
  const [venues, setVenues] = useState([]);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [membership, setMembership] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const me = await hosys.auth.me();
      // Assegurar que l'usuari té 'id' per compatibilitat amb Base44
      if (me && !me.id) {
        me.id = me.user_id;
      }
      setUser(me);

      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Fetch venues
      const token = getMemToken();
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
      const venuesResponse = await fetch(`${API_URL}/api/venues`, {
        credentials: 'include',
        headers: authHeaders,
      });
      const allVenues = await venuesResponse.json();
      
      // Assegurar que tots els venues tenen 'id' per compatibilitat
      allVenues.forEach(v => { if (!v.id) v.id = v.venue_id; });

      if (me.role === "superadmin" || SUPERADMIN_EMAILS.includes(me.email)) {
        console.log("[VenueContext] User is SUPERADMIN:", me.email);
        setUserRole("superadmin");
        setMembership(null);
        setVenues(allVenues);

        const preferred = me.active_venue_id
          ? allVenues.find(v => v.venue_id === me.active_venue_id || v.id === me.active_venue_id)
          : null;
        const activeVenue = preferred || allVenues[0] || null;
        setCurrentVenue(activeVenue);
        
        // Load subscription for active venue
        if (activeVenue) {
          await loadSubscription(activeVenue.venue_id || activeVenue.id);
        }
      } else {
        // Load memberships for this user
        const membersResponse = await fetch(`${API_URL}/api/data/venue_members?user_email=${me.email}`, {
          credentials: 'include',
          headers: authHeaders,
        });
        const memberships = await membersResponse.json();
        const activeMemberships = memberships.filter(m => m.active !== false);

        if (activeMemberships.length > 0) {
          const memberVenueIds = activeMemberships.map(m => m.venue_id);
          const memberVenues = allVenues.filter(v =>
            memberVenueIds.includes(v.venue_id) || memberVenueIds.includes(v.id)
          );
          setVenues(memberVenues);

          const preferredId = me.active_venue_id;
          const preferred = preferredId
            ? memberVenues.find(v => v.venue_id === preferredId || v.id === preferredId)
            : null;
          const active = preferred || memberVenues[0] || null;
          setCurrentVenue(active);

          const activeMembership = active
            ? activeMemberships.find(m => m.venue_id === (active.venue_id || active.id)) || activeMemberships[0]
            : activeMemberships[0];
          setMembership(activeMembership || null);
          setUserRole(activeMembership?.role || "worker");
          
          // Load subscription
          if (active) {
            await loadSubscription(active.venue_id || active.id);
          }
        } else {
          setVenues([]);
          setCurrentVenue(null);
          setUserRole(null);
          setMembership(null);
        }
      }
    } catch (e) {
      console.error("VenueContext loadData error:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async (venueId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      console.log("[VenueContext] Loading subscription for venue:", venueId);
      const token2 = getMemToken();
      const response = await fetch(`${API_URL}/api/subscriptions/${venueId}`, {
        credentials: 'include',
        headers: token2 ? { 'Authorization': `Bearer ${token2}` } : {},
      });
      if (response.ok) {
        const sub = await response.json();
        console.log("[VenueContext] Subscription loaded:", sub);
        setSubscription(sub);
      }
    } catch (e) {
      console.error("Error loading subscription:", e);
    }
  };

  const switchVenue = async (venue) => {
    if (userRole !== "superadmin") {
      const isAllowed = venues.some(v => (v.venue_id || v.id) === (venue.venue_id || venue.id));
      if (!isAllowed) return;
    }

    setCurrentVenue(venue);
    
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token3 = getMemToken();
    await fetch(`${API_URL}/api/auth/me`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token3 ? { 'Authorization': `Bearer ${token3}` } : {}),
      },
      body: JSON.stringify({ active_venue_id: venue.venue_id || venue.id })
    });

    // Update role
    if (userRole !== "superadmin") {
      const membersResponse = await fetch(`${API_URL}/api/data/venue_members?user_email=${user?.email}`, {
        credentials: 'include',
        headers: getMemToken() ? { 'Authorization': `Bearer ${getMemToken()}` } : {},
      });
      const memberships = await membersResponse.json();
      const m = memberships.find(m => m.venue_id === (venue.venue_id || venue.id) && m.active !== false);
      if (m) {
        setMembership(m);
        setUserRole(m.role);
      }
    }
    
    // Load subscription for new venue
    await loadSubscription(venue.venue_id || venue.id);
  };

  // Helper to get plan
  const plan = subscription?.plan || 'basic';

  return (
    <VenueContext.Provider value={{
      currentVenue,
      venues,
      user,
      userRole,
      membership,
      subscription,
      plan,
      loading,
      switchVenue,
      refreshData: loadData,
    }}>
      {children}
    </VenueContext.Provider>
  );
}

export function useVenue() {
  const ctx = useContext(VenueContext);
  if (!ctx) throw new Error("useVenue must be used within VenueProvider");
  return ctx;
}
